import type { ActivityItem, Design, DesignRecord, Masjid, Screen, TimetableDay } from '@noorview/core';
import { TEMPLATES, addDays, applyJamaahRule, applyTemplate, blankDesign, calculateStarts, fromMinutes, minutesOf, newId, normalizeMasjid, weekdayOf, ymd } from '@noorview/core';
import type { Member } from './backend';

/** Starting data for the sandbox: one masjid with four screens, each already showing a design. */

export interface Device { id: string; code: string | null; screenId: string | null; createdAt: string; pairedAt?: string }

export interface SandboxDb {
  version: 1;
  masjid: Masjid;
  screens: Screen[];
  designs: DesignRecord[];
  schedules: import('@noorview/core').Schedule[];
  timetable: import('@noorview/core').TimetableDay[];
  activity: ActivityItem[];
  members: Member[];
  devices: Device[];
  /** Saved layouts (added later; older sandboxes don't have it yet). */
  templates?: import('@noorview/core').SavedTemplate[];
  /** Live messages and appeals (added later; older sandboxes don't have it yet). */
  broadcasts?: import('@noorview/core').Broadcast[];
  /** Set once the example timetable has been added (prayer times come only from the timetable). */
  ttSeeded?: boolean;
}

/**
 * An example timetable so the sandbox's screens have times straight away, as if the masjid had uploaded its CSV:
 * a few weeks back to a year ahead, with two Jumu'ahs on Fridays and Ramadan times in Ramadan 1448 (Feb–Mar 2027).
 */
export function demoTimetable(masjid: Masjid, today: string): TimetableDay[] {
  const rules = { fajr: { mode: 'offset', minutes: 20, roundTo: 5 }, dhuhr: { mode: 'fixed', time: '13:30' }, asr: { mode: 'offset', minutes: 15, roundTo: 5 }, maghrib: { mode: 'offset', minutes: 5 }, isha: { mode: 'offset', minutes: 15, roundTo: 5 } } as const;
  const out: TimetableDay[] = [];
  for (let i = -14; i < 380; i++) {
    const date = addDays(today, i);
    const s = calculateStarts(masjid, date);
    const d: TimetableDay = { date, ...s };
    for (const k of ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const) { const j = applyJamaahRule(s[k], rules[k]); if (j) d[`${k}Jamaah`] = j; }
    if (weekdayOf(date) === 5) { d.jumuah1 = '13:15'; d.jumuah2 = '14:00'; }
    if (date >= '2027-02-08' && date <= '2027-03-09') { d.sehri = fromMinutes(minutesOf(s.fajr) - 10); d.tarawih = d.ishaJamaah ?? s.isha; }
    out.push(d);
  }
  return out;
}

function withContent(d: Design, today: string): Design {
  for (const w of d.widgets) {
    if (w.type === 'events') w.props = { ...w.props, items: [
      { id: newId('e'), title: 'Seerah evening', date: addDays(today, 2), time: '19:30', place: 'Main hall' },
      { id: newId('e'), title: 'Youth football', date: addDays(today, 4), time: '17:00', place: 'Lister Park' },
      { id: newId('e'), title: 'Qur’an competition', date: addDays(today, 9), time: '11:00' },
    ] };
  }
  return d;
}

const tpl = (id: string) => TEMPLATES.find((t) => t.id === id)!;

export function seedDb(now = new Date()): SandboxDb {
  const masjid = normalizeMasjid({ id: 'm_taqwa', name: 'Masjid At-Taqwa', city: 'Bradford', timezone: 'Europe/London', latitude: 53.7957, longitude: -1.7594, plan: 'basic' });
  const today = ymd(now, masjid.timezone);
  const at = now.toISOString();
  const mk = (name: string, template: string, aspect: Design['aspect'] = '16:9'): DesignRecord => {
    const design = withContent(applyTemplate(blankDesign(aspect), tpl(template)), today);
    return { id: newId('d'), masjidId: masjid.id, name, draft: design, draftRevision: 1, draftUpdatedAt: at, publishedVersion: 1, published: design, publishedAt: at };
  };
  const designs = [mk('Main Hall', 'classic'), mk('Corridor', 'prayer-focus'), mk('Downstairs', 'community'), mk('Sisters’ Hall', 'classic', '9:16')];
  const screens: Screen[] = designs.map((d) => ({ id: newId('s'), masjidId: masjid.id, name: d.name, designId: d.id, deviceId: null, power: 'on', locked: false, lastSeenAt: null, showingVersion: null, createdAt: at }));
  const minutesAgo = (m: number) => new Date(now.getTime() - m * 60_000).toISOString();
  const activity: ActivityItem[] = [
    { id: newId('a'), masjidId: masjid.id, kind: 'settings', title: 'Welcome to the NoorView sandbox', detail: 'Open a virtual screen to see changes go live', at: minutesAgo(1), read: false },
    { id: newId('a'), masjidId: masjid.id, kind: 'published', title: 'Main Hall is ready', detail: 'Huzaifah · Classic layout', at: minutesAgo(3), read: false },
  ];
  return {
    version: 1, masjid, screens, designs, schedules: [], timetable: demoTimetable(masjid, today), ttSeeded: true, activity,
    members: [{ id: 'u_owner', name: 'Huzaifah Seedat', email: 'owner@sandbox.local', role: 'owner', joinedAt: at }],
    devices: [],
  };
}
